import { useEffect, useRef } from 'react';

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('visible');
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-24 md:py-32 bg-kura-cream">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 section-reveal">
          <span
            className="text-kura-gold text-xs tracking-[0.4em] uppercase"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            About Us
          </span>
          <h2
            className="text-kura-green text-3xl md:text-4xl font-bold mt-2 tracking-wide"
            style={{ fontFamily: 'Noto Serif JP, serif' }}
          >
            私たちについて
          </h2>
          <div className="ainu-border mt-4 max-w-xs mx-auto" />
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="section-reveal">
            <div className="relative rounded-lg overflow-hidden" style={{ height: '480px' }}>
              <img
                src="https://public.readdy.ai/ai/img_res/4988e77fadc10af6951325fae914b890.jpg"
                alt="買取こんかね 店舗イメージ"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-kura-green/40 to-transparent" />
            </div>
          </div>

          {/* Text content */}
          <div className="flex flex-col gap-8">
            {/* Ainu etymology */}
            <div className="section-reveal bg-kura-green rounded-lg p-6 border border-kura-gold/30">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-kura-gold/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-leaf-line text-kura-gold text-xl" />
                </div>
                <div>
                  <h3
                    className="text-kura-gold-light text-lg font-semibold mb-2 tracking-wide"
                    style={{ fontFamily: 'Noto Serif JP, serif' }}
                  >
                    「こんかね」の由来
                  </h3>
                  <p
                    className="text-white/85 text-sm leading-relaxed tracking-wide"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    「こんかね」とは、北海道の先住民族・アイヌの言葉で「黄金」を意味します。
                    北海道の大地が育んだアイヌ文化への敬意を込め、
                    すべてのものに宿る「黄金の価値」を引き出したいという想いからこの屋号をつけました。
                  </p>
                </div>
              </div>
            </div>

            {/* Company intro */}
            <div className="section-reveal">
              <h3
                className="text-kura-green text-xl font-semibold mb-3 tracking-wide"
                style={{ fontFamily: 'Noto Serif JP, serif' }}
              >
                蔵合同会社について
              </h3>
              <p
                className="text-kura-dark/75 text-sm leading-loose tracking-wide"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                私たち蔵合同会社は、北海道を拠点に活動する古物商です。
                「どんなものにも、価値がある。」を信念に、
                催事・出張買取を通じてお客様のもとへ直接伺い、
                大切なお品物を丁寧に査定・買取いたします。
              </p>
            </div>

            {/* Philosophy */}
            <div className="section-reveal">
              <h3
                className="text-kura-green text-xl font-semibold mb-3 tracking-wide"
                style={{ fontFamily: 'Noto Serif JP, serif' }}
              >
                買取への想い
              </h3>
              <p
                className="text-kura-dark/75 text-sm leading-loose tracking-wide"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                長年眠っていた品、引越しや相続で整理が必要なお品物——
                それらには必ず「価値」があります。
                私たちは、お客様の大切な品々に正直に向き合い、
                その真の価値を正当に評価することを約束します。
                押しつけや不当な値下げは一切行いません。
              </p>
            </div>

            {/* Stats */}
            <div className="section-reveal grid grid-cols-3 gap-4">
              {[
                { num: '年中', unit: '無休', label: '出張対応' },
                { num: '0', unit: '円', label: '査定・出張費' },
                { num: '即日', unit: '支払い', label: '現金にて' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="text-center bg-kura-cream-dark rounded-md p-4 border border-kura-green/10"
                >
                  <p
                    className="text-kura-green text-xl font-bold leading-tight"
                    style={{ fontFamily: 'Noto Serif JP, serif' }}
                  >
                    {stat.num}
                    <span className="text-sm text-kura-gold ml-0.5">{stat.unit}</span>
                  </p>
                  <p
                    className="text-kura-dark/60 text-xs mt-1 tracking-wide"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
